import { ResponseErrorTypes, ResponseType} from './responseTypes';
import { ApiResponse, FatalError, ResponseError, ServiceUnavailable, Success } from './response';
import * as t from 'io-ts';
import { pipe } from 'fp-ts/lib/pipeable';
import { fold } from 'fp-ts/lib/Either';
import { failure } from 'io-ts/lib/PathReporter';
import log from 'loglevel';
import { ErrorDetails, ErrorDetailsDynamicType } from 'acscp/api/models/errorDetails';

export function processResponse<T>(response: Response,
                                   responseBody: any,
                                   resultDynamicType: t.Type<T>): ApiResponse<T> {
    if (response.ok) {
        const validationResult = resultDynamicType.decode(responseBody);
        return pipe(
            validationResult,
            fold<t.Errors, T, ApiResponse<T>>(
                errors => {
                    return new FatalError('Returned JSON object doesn\'t have the desired type.', failure(errors));
                },
                validResponse => new Success<T>(validResponse, response.headers)
            )
        );
    } else if (response.status === 429 || response.status === 503 || response.status === 504) {
        return new ServiceUnavailable(response.statusText, responseBody);
    } else {
        let errorType: ResponseErrorTypes;

        if (response.status === 401) {
            errorType = ResponseType.Unauthorized;
        } else if (response.status === 403) {
            errorType = ResponseType.Forbidden;
        } else if (response.status === 400) {
            errorType = ResponseType.RequestError;
        } else if (response.status === 404) {
            errorType = ResponseType.NotFound;
        } else if (response.status === 409) {
            errorType = ResponseType.Conflict;
        } else if (response.status >= 500 && response.status < 600) {
            errorType = ResponseType.ServerError;
        } else {
            log.error(`Unhandled response code: ${response.status}`);
            errorType = ResponseType.UnknownError;
        }

        const validatedError = ErrorDetailsDynamicType.decode(responseBody);
        return pipe(
            validatedError,
            fold<t.Errors, ErrorDetails, ApiResponse<T>>(
                failedErrorParse => new FatalError('Returned error JSON object doesn\'t have expected error shape.',
                    failure(failedErrorParse)),
                validErrorBody => new ResponseError(errorType, response.statusText, validErrorBody, response.headers)
            )
        );
    }
}
